<template>
  <v-app>
    <div v-if="(isLogged && !shouldBeDisabled) && !shouldResetPassword" class="fill-height">
      <SocketRoom />
      <the-navigation
        @toggle-navigation="showNavigation = !showNavigation"
        :show-navigation="showNavigation"
      />
      <the-header @toggle-navigation="showNavigation = !showNavigation" />
      <the-alert v-if="showAlert" />
      <the-dialog v-if="showDialog" />
      <v-main class="fill-height">
        <div class="fill-height px-4 pt-4">
          <v-alert
            v-if="alert !== null"
            :color="alert.color"
            dense
            dismissible
            outlined
            text
            :type="alert.type"
            class="top-alert"
            @close="closeAlert"
          >{{ alert.msg }}</v-alert>
          <router-view />
        </div>
      </v-main>
    </div>
    <div v-else class="fill-height">
      <v-main class="fill-height">
        <router-view />
      </v-main>
    </div>
  </v-app>
</template>

<script>
  import { mapActions, mapState } from 'vuex';
  import { SettingsService } from '@/utils/services/settings-service';

  import { VOTE_CONTENT_TYPES } from '@/config/session';
  import TheNavigation from './components/__single/TheNav/TheNav';
  import TheHeader from './components/__single/TheHeader';
  import TheAlert from './components/__single/TheAlert';
  import TheDialog from './components/__single/TheDialog';
  import SocketRoom from './components/__base/SocketRoom';
  import * as roles from './utils/user-roles';
  import { alertEventBus } from "@/utils/alert-event-bus";

  export default {
    name: 'App',
    components: {
      TheAlert,
      TheDialog,
      TheHeader,
      TheNavigation,
      SocketRoom,
    },
    sockets: {
      vote(data) {
        if (
          this.isLogged
          && this.userRoles.includes('ROLE_VOTE_VIEWER')
          && this.$route.name !== 'vote-active'
          && data.type === VOTE_CONTENT_TYPES.VOTING
        ) {
          this.$router.push({ name: 'vote-active' });
        }
      },
    },
    data() {
      return {
        showNavigation: true,
        loading: true,
        roles,
        alert: null,
      };
    },
    computed: {
      ...mapState('auth', ['isLogged', 'userRoles', 'username', 'shouldResetPassword']),
      ...mapState('alert', ['showAlert']),
      ...mapState('dialog', ['showDialog']),

      shouldBeDisabled() {
        return (
          this.$route.path.startsWith('/voter')
          || this.$route.path.startsWith('/sessions/print-protocol')
        );
      },
    },
    watch: {
      alert(val) {
        if(val){
          setTimeout(()=>{this.closeAlert()},10000)
        }
      },
      isLogged(val) {
        if (val) {
          this.getBaseConfig();
          if (this.userRoles.includes(roles.ROLE_VOTE_MANAGER)) {
            this.enableLiveChatScript().then(() => {
              this.setLiveChatUserName();
            });
          }
        } else {
          this.disableLiveChatScript();
          this.$socket.disconnect();
        }
      },
    },
    created() {
      this.getBaseConfig();
      if (this.isLogged) {
        if (
          this.userRoles.includes(roles.ROLE_VOTE_MANAGER)
          && !window.location.pathname.includes('voter/projector')
          && !window.location.pathname.includes('sessions/print-protocol/')
        ) {
          this.enableLiveChatScript().then(() => {
            this.setLiveChatUserName();
          });
        }
      }

      alertEventBus.eventBus.$on('alert', (alert) => {this.alert = alert})
    },
    methods: {
      ...mapActions('config', ['setConfig']),
      closeAlert() {
        this.alert = null;
      },
      getBaseConfig() {
        SettingsService.getConfig()
          .then(res => {
            this.loading = false;
            this.setConfig(res.data.settings);
          })
          .catch(err => {
            console.error(err);
            this.loading = false;
          });
      },
      enableLiveChatScript() {
        return new Promise(resolve => {
          const existingScript = document.getElementById('live-chat');

          if (!existingScript) {
            const liveChatScript = document.createElement('script');
            liveChatScript.type = 'text/javascript';
            liveChatScript.id = 'live-chat';

            const scriptValue = document.createTextNode(`
              (function(w, d, s, u) {
                w.RocketChat = function(c) { w.RocketChat._.push(c) }; w.RocketChat._ = []; w.RocketChat.url = u;
                var h = d.getElementsByTagName(s)[0], j = d.createElement(s);
                j.async = true; j.src = 'https://czat.aztv.pl/livechat/rocketchat-livechat.min.js?_=201903270000';
                j.id = 'live-chat-2';
                h.parentNode.insertBefore(j, h);
              })(window, document, 'script', 'https://czat.aztv.pl/livechat');
            `);

            liveChatScript.appendChild(scriptValue);
            document.head.appendChild(liveChatScript);

            resolve();
          } else {
            resolve();
          }
        });
      },
      disableLiveChatScript() {
        const liveChatScript = document.getElementById('live-chat');
        const liveChatScript2 = document.getElementById('live-chat-2');

        if (liveChatScript && liveChatScript2) {
          liveChatScript.parentNode.removeChild(liveChatScript);
          liveChatScript2.parentNode.removeChild(liveChatScript2);

          window.location.reload();
        }
      },
      setLiveChatUserName() {
        const { username } = this;
        window.RocketChat(function() {
          const domain = window.location.host.split('.');
          const fullUserName = `${username} - ${domain[0]}`;
          this.setGuestName(fullUserName);
        });
      },
    },
    beforeDestroy() {
      alertEventBus.eventBus.$off('alert')
    }
  };
</script>

<style>
  .alert {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 30px;
  }
  .top-alert {
    margin-top: 10px;
  }
</style>
